import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { GOOGLE_CODES, GoogleCalendarService } from '../../rest_services/google-calendar.service';

declare var NotificationFx: any;

/**
 * MF-1879 : information d'onboarding affichée sur les écrans de planification,
 * avec la même notification que les rappels existants (NotificationFx, layout "attached", effet "bouncyflip").
 * Elle invite l'utilisateur à synchroniser ses visites avec Google Agenda depuis son profil.
 *
 * Affichée uniquement si :
 *  - l'intégration est activée sur le serveur ;
 *  - le compte Google n'est pas connecté (ou l'autorisation a expiré) ;
 *  - l'utilisateur ne l'a pas masquée : fermeture = pour la session, "Ne plus afficher" = définitivement.
 */
@Component({
  selector: 'app-google-agenda-hint',
  templateUrl: './google-agenda-hint.component.html'
})
export class GoogleAgendaHintComponent implements OnInit, OnDestroy {

  private static readonly SESSION_KEY = 'mf_gcal_hint_later';
  private static readonly SHOW_DELAY_MS = 1500;
  /** Durée d'affichage avant fermeture automatique. */
  private static readonly TTL_MS = 30000;
  /** Si une autre notification (ex. rappel "Aucune visite") est affichée, on attend qu'elle se ferme. */
  private static readonly RETRY_MS = 2000;
  private static readonly MAX_RETRIES = 30;

  private notification: any = null;
  private timer: any;
  private sub?: Subscription;
  private destroyed = false;

  constructor(private googleService: GoogleCalendarService, private router: Router, private zone: NgZone) { }

  ngOnInit(): void {
    if (this.isDismissed() || typeof NotificationFx === 'undefined') { return; }
    this.sub = this.googleService.getStatus().subscribe(res => {
      if (res.code !== GOOGLE_CODES.STATUS_SUCCESS || res.connected) { return; }
      const revoked = res.status === 'REVOKED';
      this.timer = setTimeout(() => this.showWhenFree(revoked, 0), GoogleAgendaHintComponent.SHOW_DELAY_MS);
    }, () => { /* silencieux : l'information est optionnelle */ });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.timer);
    this.sub?.unsubscribe();
    // on ne laisse pas la notification flotter sur les autres pages
    if (this.notification && this.notification.active) { this.notification.dismiss(); }
  }

  private showWhenFree(revoked: boolean, attempt: number): void {
    if (this.destroyed) { return; }
    const otherVisible = document.querySelector('.ns-box.ns-show');
    if (otherVisible && attempt < GoogleAgendaHintComponent.MAX_RETRIES) {
      this.timer = setTimeout(() => this.showWhenFree(revoked, attempt + 1), GoogleAgendaHintComponent.RETRY_MS);
      return;
    }
    this.show(revoked);
  }

  private show(revoked: boolean): void {
    const name = this.escape(localStorage.getItem('fullname') || sessionStorage.getItem('fullname') || '');
    const hello = name ? 'Bonjour <b>' + name + ' </b><br>' : '';

    const message = revoked
      ? '<img src="assets/img/suivitim_img/calendar_white.gif" style="width: 50px;">' +
        '<b>GOOGLE AGENDA</b> : <br><p>' + hello +
        'La synchronisation de vos visites avec Google Agenda est interrompue : ' +
        'l\'autorisation Google a expiré. <br>Reconnectez votre compte depuis votre profil. <br><br>' +
        '<img src="assets/img/suivitim_img/toright.gif" style="width: 25px;">' +
        '<a href="profil?tab=google" id="gah-activate">  Reconnecter Google Agenda</a><br>' +
        '<a href="#" id="gah-never" style="font-size: 11px; opacity: .8; text-decoration: underline;">Ne plus afficher ce message</a></p>'
      : '<img src="assets/img/suivitim_img/calendar_white.gif" style="width: 50px;">' +
        '<b>NOUVEAU</b> : <br><p>' + hello +
        'Les visites que vous créerez ou planifierez <b>après l\'activation</b> peuvent apparaître automatiquement ' +
        'dans votre <b>Google Agenda</b>, et être mises à jour en cas de report ou d\'annulation ! <br>' +
        'Activez la synchronisation depuis votre profil. <br><br>' +
        '<img src="assets/img/suivitim_img/toright.gif" style="width: 25px;">' +
        '<a href="profil?tab=google" id="gah-activate">  Activer Google Agenda</a><br>' +
        '<a href="#" id="gah-never" style="font-size: 11px; opacity: .8; text-decoration: underline;">Ne plus afficher ce message</a></p>';

    this.notification = new NotificationFx({
      message: message,
      layout: 'attached',
      effect: 'bouncyflip',
      type: revoked ? 'warning' : 'notice', // notice, warning or error
      ttl: GoogleAgendaHintComponent.TTL_MS,
      onClose: () => this.remember(false) // croix ou fermeture automatique : réaffichée à la prochaine session
    });
    this.notification.show();
    this.bindLinks();
  }

  /** Branche les liens du message (navigation Angular sans rechargement de page). */
  private bindLinks(): void {
    const box: HTMLElement | null = this.notification && this.notification.ntf;
    if (!box) { return; }
    const activate = box.querySelector('#gah-activate');
    const never = box.querySelector('#gah-never');
    if (activate) {
      activate.addEventListener('click', (e: Event) => {
        e.preventDefault();
        this.remember(false);
        this.notification.dismiss();
        this.zone.run(() => this.router.navigate(['/profil'], { queryParams: { tab: 'google' } }));
      });
    }
    if (never) {
      never.addEventListener('click', (e: Event) => {
        e.preventDefault();
        this.remember(true);
        this.notification.dismiss();
      });
    }
  }

  private remember(forever: boolean): void {
    try {
      if (forever) {
        localStorage.setItem(this.permanentKey(), '1');
      }
      sessionStorage.setItem(GoogleAgendaHintComponent.SESSION_KEY, '1');
    } catch (e) { /* stockage indisponible : l'information réapparaîtra, sans gravité */ }
  }

  private isDismissed(): boolean {
    try {
      return localStorage.getItem(this.permanentKey()) === '1'
        || sessionStorage.getItem(GoogleAgendaHintComponent.SESSION_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  /** Clé propre à l'utilisateur connecté (plusieurs comptes peuvent partager un poste). */
  private permanentKey(): string {
    const user = localStorage.getItem('logged') || sessionStorage.getItem('logged') || '';
    return 'mf_gcal_hint_never_' + user;
  }

  private escape(text: string): string {
    return text.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' } as any)[c]);
  }
}
